import { HttpClient } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { JSONContent } from '@tiptap/core'
import { firstValueFrom } from 'rxjs'
import type { CitationMinuteNodes } from '../../../../../backend/src/citations/minutes-citations-api.dto'
import { MinutesVerboseness } from '../../meetings.service'

export type RevisionSummary = {
  id: number
  createdAt: string
  authorName: string
  otherAuthorCount: number
  systemGenerated: boolean
  minutesVerboseness: MinutesVerboseness | null
  isCurrent: boolean
}

export type RevisionDownloadEvent = {
  revision: RevisionSummary
  format: 'pdf' | 'docx'
}

export type RevisionContent = {
  content: JSONContent
  citations: CitationMinuteNodes | null
}

export type RestrictedItem = {
  templateItemId: string
  title: string
  /** The item's outline number in the minutes, e.g. `[2, 1]` for "2.1"; null before the minutes place it. */
  number: number[] | null
}

export function entityParams(meetingEntityId: number | null): Record<string, number> {
  return meetingEntityId === null ? {} : { meetingEntityId }
}

@Injectable({ providedIn: 'root' })
export class EditorRevisionsService {
  private http = inject(HttpClient)

  listRevisions(meetingId: number, meetingEntityId: number | null = null): Promise<RevisionSummary[]> {
    return firstValueFrom(this.http.get<RevisionSummary[]>(`/api/tiptap-editor/meetings/${meetingId}/revisions`, { params: entityParams(meetingEntityId) }))
  }

  getRevision(meetingId: number, revisionId: number, meetingEntityId: number | null = null): Promise<RevisionContent> {
    return firstValueFrom(
      this.http.get<RevisionContent>(`/api/tiptap-editor/meetings/${meetingId}/revisions/${revisionId}`, { params: entityParams(meetingEntityId) })
    )
  }

  restoreRevision(meetingId: number, revisionId: number, meetingEntityId: number | null = null): Promise<RevisionContent> {
    return firstValueFrom(
      this.http.post<RevisionContent>(`/api/tiptap-editor/meetings/${meetingId}/revisions/${revisionId}/restore`, {}, { params: entityParams(meetingEntityId) })
    )
  }

  getRestrictedItems(meetingId: number): Promise<RestrictedItem[]> {
    return firstValueFrom(this.http.get<RestrictedItem[]>(`/api/tiptap-editor/meetings/${meetingId}/restricted-items`))
  }
}
